export type CourseUnit = {
  id: string;
  type: string;
  solution?: string;
  [key: string]: any;
};

export type Course = {
  units: CourseUnit[];
  [key: string]: any;
};

export type ExerciseEvaluation = {
  exerciseId: string;
  success: boolean;
};

/**
 * Records (or replaces) the evaluation of an exercise unit's selected option, keeping at most
 * one entry per exercise -- answering the same exercise again (e.g. after navigating back to
 * it) updates its result instead of accumulating duplicate entries.
 */
export function evaluateOption(
  evaluation: ExerciseEvaluation[],
  unit: CourseUnit,
  selectedOption: string,
): ExerciseEvaluation[] {
  const withoutUnit = evaluation.filter(result => result.exerciseId !== unit.id);
  return [...withoutUnit, { exerciseId: unit.id, success: unit.solution === selectedOption }];
}

/**
 * Clears a unit's recorded evaluation, e.g. when navigating back past an exercise so it can be
 * answered again.
 */
export function removeEvaluation(evaluation: ExerciseEvaluation[], unitId: string): ExerciseEvaluation[] {
  return evaluation.filter(result => result.exerciseId !== unitId);
}

/**
 * Percentage (0-100) of the course's exercise units answered correctly. An exercise that was
 * never answered (e.g. skipped via "Weiter" without selecting an option) counts as incorrect
 * rather than being excluded or causing an error.
 */
export function evaluateCourse(course: Course, evaluation: ExerciseEvaluation[]): number {
  const exerciseUnits = course.units.filter(unit => unit.type === "exercise");
  if (exerciseUnits.length === 0) {
    return 100;
  }
  const correctlySolvedUnits = exerciseUnits.filter(exercise =>
    evaluation.some(result => result.exerciseId === exercise.id && result.success)
  );
  return correctlySolvedUnits.length / exerciseUnits.length * 100;
}
